import { useTranslations } from '@/i18n/use-translations';
import { EVENT_SPACE_TYPE } from '@/types/dtos/events/event-space.enum';
import {
  Box,
  TextField,
  IconButton,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Controller, useFieldArray, type UseFormReturn, useWatch } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DeleteOutlined, ExpandMore, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEventStore } from '@/state/stores/use-event-store';
import { updateEventSchemaFactory } from '@/schemas/event/update-event';
import type { z } from 'zod';
import { useEffect } from 'react';

export default function SpacesForm({
  form,
}: {
  form: UseFormReturn<z.infer<ReturnType<typeof updateEventSchemaFactory>>>;
}) {
  const event = useEventStore((s) => s.event);
  const t = useTranslations();
  const startDate = useWatch({ control: form.control, name: 'startDate' });
  const durationDays = useWatch({ control: form.control, name: 'durationDays' });
  const spaces = useWatch({ control: form.control, name: 'spaces' });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'spaces',
  });

  useEffect(() => {
    if (!startDate || !durationDays || !spaces?.length) return;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + durationDays - 1);

    form.setValue('endDate', endDate.toISOString(), { shouldValidate: true });

    spaces.forEach((space, index) => {
      if (!space.fromDate || !space.tillDate) {
        form.setValue(`spaces.${index}.fromDate`, startDate, { shouldValidate: true });
        form.setValue(`spaces.${index}.tillDate`, endDate.toISOString(), { shouldValidate: true });
        return;
      }

      const oldStartDate = form.getValues('startDate');
      if (!oldStartDate) return;

      // Only update if the start date has actually changed
      if (oldStartDate === startDate) return;

      const fromDateOffset = Math.floor(
        (new Date(space.fromDate).getTime() - new Date(oldStartDate).getTime()) / (1000 * 60 * 60 * 24),
      );
      const tillDateOffset = Math.floor(
        (new Date(space.tillDate).getTime() - new Date(oldStartDate).getTime()) / (1000 * 60 * 60 * 24),
      );

      const newFromDate = new Date(startDate);
      newFromDate.setDate(newFromDate.getDate() + fromDateOffset);

      const newTillDate = new Date(startDate);
      newTillDate.setDate(newTillDate.getDate() + tillDateOffset);

      form.setValue(`spaces.${index}.fromDate`, newFromDate.toISOString(), { shouldValidate: true });
      form.setValue(`spaces.${index}.tillDate`, newTillDate.toISOString(), { shouldValidate: true });
    });
  }, [startDate, durationDays, spaces, form]);

  const addNewSpace = () => {
    if (!event) return;
    const currentEndDate = form.getValues('endDate') || event.endDate;
    const currentStartDate = form.getValues('startDate') || event.startDate;
    append({
      type: EVENT_SPACE_TYPE.STAGE,
      name: 'New Space',
      description: '',
      capacity: 0,
      fromDate: currentStartDate,
      tillDate: currentEndDate,
      minTimeGap: 0,
      location: '',
    });
  };

  if (!event) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
          px: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          <IconButton onClick={addNewSpace} size="small" aria-label="Add Space">
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 4, flex: 1 }}>
            <Typography sx={{ flexGrow: 1, minWidth: '100px' }}>Name</Typography>
            <Typography sx={{ flexGrow: 1, minWidth: '60px' }}>Capacity</Typography>
            <Typography sx={{ flexGrow: 1, minWidth: '60px' }}>Gap</Typography>
          </Box>
          <Box sx={{ width: 24, mr: 1 }} />
        </Box>
      </Box>

      <Stack spacing={0}>
        {fields.map((field, index) => (
          <Accordion key={field.id}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  margin: 0,
                  px: 1,
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  marginRight: 1,
                },
              }}
            >
              <Controller
                name={`spaces.${index}.name`}
                control={form.control}
                render={({ field: nameField }) => (
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: '100px' }}>
                    {nameField.value || 'Unnamed Space'}
                  </Typography>
                )}
              />
              <Controller
                name={`spaces.${index}.capacity`}
                control={form.control}
                render={({ field: capField }) => (
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: '60px' }}>
                    {capField.value} Pax
                  </Typography>
                )}
              />
              <Controller
                name={`spaces.${index}.minTimeGap`}
                control={form.control}
                render={({ field: timeField }) => (
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: '60px' }}>
                    {timeField.value} min.
                  </Typography>
                )}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Controller
                    name={`spaces.${index}.name`}
                    control={form.control}
                    render={({ field: nameField, fieldState }) => (
                      <TextField
                        {...nameField}
                        variant="standard"
                        size="small"
                        label={t('event.detail.settingsMenu.spaces.name')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    name={`spaces.${index}.capacity`}
                    control={form.control}
                    render={({ field: capField, fieldState }) => (
                      <TextField
                        {...capField}
                        type="number"
                        variant="standard"
                        size="small"
                        label={t('event.detail.settingsMenu.spaces.capacity')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ width: '120px' }}
                      />
                    )}
                  />

                  <Controller
                    name={`spaces.${index}.minTimeGap`}
                    control={form.control}
                    render={({ field: timeField, fieldState }) => (
                      <TextField
                        {...timeField}
                        type="number"
                        variant="standard"
                        size="small"
                        label={t('event.detail.settingsMenu.spaces.minTimeGap')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ width: '120px' }}
                        InputProps={{
                          endAdornment: <AccessTimeIcon color="action" fontSize="small" />,
                        }}
                      />
                    )}
                  />
                  <IconButton onClick={() => remove(index)} size="small" color="error">
                    <DeleteOutlined />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name={`spaces.${index}.type`}
                    control={form.control}
                    render={({ field: typeField, fieldState }) => (
                      <FormControl variant="standard" size="small" error={!!fieldState.error} sx={{ minWidth: 120 }}>
                        <InputLabel>{t('event.detail.settingsMenu.spaces.type')}</InputLabel>
                        <Select {...typeField} value={typeField.value || EVENT_SPACE_TYPE.STAGE}>
                          {Object.values(EVENT_SPACE_TYPE).map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name={`spaces.${index}.fromDate`}
                    control={form.control}
                    render={({ field: { value, onChange }, fieldState }) => (
                      <DatePicker
                        label={t('event.detail.settingsMenu.spaces.fromDate')}
                        value={value ? new Date(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue?.toISOString() ?? undefined);
                        }}
                        minDate={event.startDate ? new Date(event.startDate) : undefined}
                        maxDate={event.endDate ? new Date(event.endDate) : undefined}
                        slotProps={{
                          textField: {
                            variant: 'standard',
                            size: 'small',
                            fullWidth: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`spaces.${index}.tillDate`}
                    control={form.control}
                    render={({ field: { value, onChange }, fieldState }) => (
                      <DatePicker
                        label={t('event.detail.settingsMenu.spaces.tillDate')}
                        value={value ? new Date(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue?.toISOString() ?? undefined);
                        }}
                        minDate={event.startDate ? new Date(event.startDate) : undefined}
                        maxDate={event.endDate ? new Date(event.endDate) : undefined}
                        slotProps={{
                          textField: {
                            variant: 'standard',
                            size: 'small',
                            fullWidth: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                <Controller
                  name={`spaces.${index}.description`}
                  control={form.control}
                  render={({ field: descField, fieldState }) => (
                    <TextField
                      {...descField}
                      variant="standard"
                      label={t('common.description')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={4}
                    />
                  )}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <InfoOutlined color="action" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          {t('common.description')}
        </Typography>
      </Box>
    </Box>
  );
}
